import { DiscriminatedUnionTypeInstance, Severity } from "@fern-api/browser-compatible-base-generator";
import { assertNever } from "@fern-api/core-utils";
import { FernIr } from "@fern-api/dynamic-ir-sdk";
import { php } from "@fern-api/php-codegen";

import { DynamicSnippetsGeneratorContext } from "./DynamicSnippetsGeneratorContext.js";

export declare namespace DynamicTypeLiteralMapper {
    interface Args {
        typeReference: FernIr.dynamic.TypeReference;
        value: unknown;
        as?: ConvertedAs;
    }

    // Identifies what the type is being converted as, which sometimes influences how
    // the type is instantiated.
    type ConvertedAs = "key";
}

export class DynamicTypeLiteralMapper {
    private context: DynamicSnippetsGeneratorContext;

    constructor({ context }: { context: DynamicSnippetsGeneratorContext }) {
        this.context = context;
    }

    public convert(args: DynamicTypeLiteralMapper.Args): php.TypeLiteral {
        // eslint-disable-next-line eqeqeq
        if (args.value === null) {
            if (this.context.isNullable(args.typeReference)) {
                return php.TypeLiteral.null();
            }
            this.context.errors.add({
                severity: Severity.Critical,
                message: "Expected non-null value, but got null"
            });
            return php.TypeLiteral.nop();
        }
        if (args.value === undefined) {
            return php.TypeLiteral.nop();
        }
        switch (args.typeReference.type) {
            case "list":
            case "set":
                return this.convertList({ list: args.typeReference.value, value: args.value });
            case "literal":
                return this.convertLiteral({ literalType: args.typeReference.value, value: args.value });
            case "map":
                return this.convertMap({ map: args.typeReference, value: args.value });
            case "named": {
                const named = this.context.resolveNamedType({ typeId: args.typeReference.value });
                if (named == null) {
                    return php.TypeLiteral.nop();
                }
                return this.convertNamed({ named, value: args.value, as: args.as });
            }
            case "optional":
                return this.convert({ typeReference: args.typeReference.value, value: args.value, as: args.as });
            case "nullable":
                return this.convert({ typeReference: args.typeReference.value, value: args.value, as: args.as });
            case "primitive":
                return this.convertPrimitive({ primitive: args.typeReference.value, value: args.value, as: args.as });
            case "unknown":
                return this.convertUnknown({ value: args.value });
            default:
                assertNever(args.typeReference);
        }
    }

    private convertLiteral({
        literalType,
        value
    }: {
        literalType: FernIr.dynamic.LiteralType;
        value: unknown;
    }): php.TypeLiteral {
        switch (literalType.type) {
            case "boolean": {
                const bool = this.context.getValueAsBoolean({ value });
                if (bool == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.boolean(bool);
            }
            case "string": {
                const str = this.context.getValueAsString({ value });
                if (str == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.string(str);
            }
            default:
                assertNever(literalType);
        }
    }

    private convertList({ list, value }: { list: FernIr.dynamic.TypeReference; value: unknown }): php.TypeLiteral {
        if (!Array.isArray(value)) {
            this.context.errors.add({
                severity: Severity.Critical,
                message: `Expected array but got: ${typeof value}`
            });
            return php.TypeLiteral.nop();
        }
        return php.TypeLiteral.list({
            values: value.map((v, index) => {
                this.context.errors.scope({ index });
                try {
                    return this.convert({ typeReference: list, value: v });
                } finally {
                    this.context.errors.unscope();
                }
            })
        });
    }

    private convertMap({ map, value }: { map: FernIr.dynamic.MapType; value: unknown }): php.TypeLiteral {
        if (typeof value !== "object" || value == null) {
            this.context.errors.add({
                severity: Severity.Critical,
                message: `Expected object but got: ${value == null ? "null" : typeof value}`
            });
            return php.TypeLiteral.nop();
        }
        return php.TypeLiteral.map({
            entries: Object.entries(value).map(([key, value]) => {
                this.context.errors.scope(key);
                try {
                    return {
                        key: this.convert({ typeReference: map.key, value: key, as: "key" }),
                        value: this.convert({ typeReference: map.value, value })
                    };
                } finally {
                    this.context.errors.unscope();
                }
            })
        });
    }

    private convertNamed({
        named,
        value,
        as
    }: {
        named: FernIr.dynamic.NamedType;
        value: unknown;
        as?: DynamicTypeLiteralMapper.ConvertedAs;
    }): php.TypeLiteral {
        switch (named.type) {
            case "alias": {
                return this.convert({ typeReference: named.typeReference, value, as });
            }
            case "discriminatedUnion":
                return this.convertDiscriminatedUnion({
                    discriminatedUnion: named,
                    value
                });
            case "enum":
                return this.convertEnum({ enum_: named, value });
            case "object":
                return this.convertObject({ object_: named, value });
            case "undiscriminatedUnion":
                return this.convertUndiscriminatedUnion({ undiscriminatedUnion: named, value });
            default:
                assertNever(named);
        }
    }

    private convertDiscriminatedUnion({
        discriminatedUnion,
        value
    }: {
        discriminatedUnion: FernIr.dynamic.DiscriminatedUnionType;
        value: unknown;
    }): php.TypeLiteral {
        const discriminatedUnionTypeInstance = this.context.resolveDiscriminatedUnionTypeInstance({
            discriminatedUnion,
            value
        });
        if (discriminatedUnionTypeInstance == null) {
            return php.TypeLiteral.nop();
        }
        const unionVariant = discriminatedUnionTypeInstance.singleDiscriminatedUnionType;
        const unionProperties = this.convertDiscriminatedUnionProperties({
            discriminatedUnionTypeInstance,
            unionVariant
        });
        if (unionProperties == null) {
            return php.TypeLiteral.nop();
        }
        return php.TypeLiteral.reference(
            php.codeblock((writer) => {
                writer.writeNode(
                    php.invokeMethod({
                        on: php.classReference({
                            name: this.context.getClassName(discriminatedUnion.declaration.name),
                            namespace: this.context.getTypesNamespace(discriminatedUnion.declaration.fernFilepath)
                        }),
                        method: this.context.getMethodName(unionVariant.discriminantValue.name),
                        arguments_: this.convertDiscriminatedUnionVariantArgs({
                            discriminatedUnionTypeInstance,
                            unionVariant,
                            unionProperties
                        }),
                        static_: true
                    })
                );
            })
        );
    }

    private convertDiscriminatedUnionProperties({
        discriminatedUnionTypeInstance,
        unionVariant
    }: {
        discriminatedUnionTypeInstance: DiscriminatedUnionTypeInstance;
        unionVariant: FernIr.dynamic.SingleDiscriminatedUnionType;
    }): php.ConstructorField[] | undefined {
        switch (unionVariant.type) {
            case "samePropertiesAsObject": {
                const named = this.context.resolveNamedType({
                    typeId: unionVariant.typeId
                });
                if (named == null) {
                    return undefined;
                }
                const converted = this.convertNamed({ named, value: discriminatedUnionTypeInstance.value });
                if (!converted.isClass()) {
                    this.context.errors.add({
                        severity: Severity.Critical,
                        message: "Internal error; expected union value to be an object"
                    });
                    return undefined;
                }
                const object_ = converted.asClassOrThrow();
                return object_.fields;
            }
            case "singleProperty": {
                try {
                    this.context.errors.scope(unionVariant.discriminantValue.wireValue);
                    const record = this.context.getRecord(discriminatedUnionTypeInstance.value);
                    if (record == null) {
                        return [
                            {
                                name: this.context.getPropertyName(unionVariant.discriminantValue.name),
                                value: this.convert({
                                    typeReference: unionVariant.typeReference,
                                    value: discriminatedUnionTypeInstance.value
                                })
                            }
                        ];
                    }
                    return [
                        {
                            name: this.context.getPropertyName(unionVariant.discriminantValue.name),
                            value: this.convert({
                                typeReference: unionVariant.typeReference,
                                value: record[unionVariant.discriminantValue.wireValue]
                            })
                        }
                    ];
                } finally {
                    this.context.errors.unscope();
                }
            }
            case "noProperties":
                return [];
            default:
                assertNever(unionVariant);
        }
    }

    private convertDiscriminatedUnionVariantArgs({
        discriminatedUnionTypeInstance,
        unionVariant,
        unionProperties
    }: {
        discriminatedUnionTypeInstance: DiscriminatedUnionTypeInstance;
        unionVariant: FernIr.dynamic.SingleDiscriminatedUnionType;
        unionProperties: php.ConstructorField[];
    }): php.AstNode[] {
        const baseFields = this.getBaseFields({
            discriminatedUnionTypeInstance,
            singleDiscriminatedUnionType: unionVariant
        });
        if (unionVariant.type === "singleProperty") {
            const record = this.context.getRecord(discriminatedUnionTypeInstance.value);
            if (record == null && unionProperties.length === 1) {
                // The union is a single value without any base properties, e.g.
                return [
                    ...baseFields,
                    this.convert({
                        typeReference: unionVariant.typeReference,
                        value: discriminatedUnionTypeInstance.value
                    })
                ];
            }
        }
        if (unionVariant.type === "samePropertiesAsObject") {
            const named = this.context.resolveNamedType({
                typeId: unionVariant.typeId
            });
            if (named == null) {
                return [];
            }
            return [
                ...baseFields,
                php.TypeLiteral.class_({
                    reference: php.classReference({
                        name: this.context.getClassName(named.declaration.name),
                        namespace: this.context.getTypesNamespace(named.declaration.fernFilepath)
                    }),
                    fields: unionProperties
                })
            ];
        }
        return baseFields;
    }

    private getBaseFields({
        discriminatedUnionTypeInstance,
        singleDiscriminatedUnionType
    }: {
        discriminatedUnionTypeInstance: DiscriminatedUnionTypeInstance;
        singleDiscriminatedUnionType: FernIr.dynamic.SingleDiscriminatedUnionType;
    }): php.AstNode[] {
        const properties = this.context.associateByWireValue({
            parameters: singleDiscriminatedUnionType.properties ?? [],
            values: this.context.getRecord(discriminatedUnionTypeInstance.value) ?? {},

            // We're only selecting the base properties here. The rest of the properties
            // are handled by the union variant.
            ignoreMissingParameters: true
        });
        return properties.map((property) => {
            this.context.errors.scope(property.name.wireValue);
            try {
                return this.convert(property);
            } finally {
                this.context.errors.unscope();
            }
        });
    }

    private convertObject({ object_, value }: { object_: FernIr.dynamic.ObjectType; value: unknown }): php.TypeLiteral {
        const record = this.context.getRecord(value) ?? {};
        const properties = this.context.associateByWireValue({
            parameters: object_.properties,
            values: record
        });
        const fields: php.ConstructorField[] = properties.map((property) => {
            this.context.errors.scope(property.name.wireValue);
            try {
                return {
                    name: this.context.getPropertyName(property.name.name),
                    value: this.convert(property)
                };
            } finally {
                this.context.errors.unscope();
            }
        });

        const providedKeys = new Set(Object.keys(record));
        for (const param of object_.properties) {
            if (!providedKeys.has(param.name.wireValue) && !this.isOptionalOrNullable(param.typeReference)) {
                const placeholder = this.generatePlaceholderValue(param.typeReference);
                if (!php.TypeLiteral.isNop(placeholder)) {
                    fields.push({
                        name: this.context.getPropertyName(param.name.name),
                        value: placeholder
                    });
                }
            }
        }

        return php.TypeLiteral.class_({
            reference: php.classReference({
                name: this.context.getClassName(object_.declaration.name),
                namespace: this.context.getTypesNamespace(object_.declaration.fernFilepath)
            }),
            fields
        });
    }

    private isOptionalOrNullable(typeReference: FernIr.dynamic.TypeReference): boolean {
        return this.context.isOptional(typeReference) || this.context.isNullable(typeReference);
    }

    private generatePlaceholderValue(typeReference: FernIr.dynamic.TypeReference): php.TypeLiteral {
        switch (typeReference.type) {
            case "primitive":
                return this.generatePrimitivePlaceholder(typeReference.value);
            case "list":
            case "set": {
                const elementPlaceholder = this.generatePlaceholderValue(typeReference.value);
                if (php.TypeLiteral.isNop(elementPlaceholder)) {
                    return php.TypeLiteral.list({ values: [] });
                }
                return php.TypeLiteral.list({ values: [elementPlaceholder] });
            }
            case "map": {
                const keyPlaceholder = this.generatePlaceholderValue(typeReference.key);
                const valuePlaceholder = this.generatePlaceholderValue(typeReference.value);
                if (php.TypeLiteral.isNop(keyPlaceholder) || php.TypeLiteral.isNop(valuePlaceholder)) {
                    return php.TypeLiteral.map({ entries: [] });
                }
                return php.TypeLiteral.map({
                    entries: [{ key: keyPlaceholder, value: valuePlaceholder }]
                });
            }
            case "named": {
                const named = this.context.resolveNamedType({ typeId: typeReference.value });
                if (named == null) {
                    return php.TypeLiteral.nop();
                }
                return this.generateNamedPlaceholder(named);
            }
            case "optional":
            case "nullable":
                return php.TypeLiteral.null();
            case "literal":
                return this.generateLiteralPlaceholder(typeReference.value);
            case "unknown":
                return php.TypeLiteral.null();
            default:
                assertNever(typeReference);
        }
    }

    private generatePrimitivePlaceholder(primitive: FernIr.dynamic.PrimitiveTypeV1): php.TypeLiteral {
        switch (primitive) {
            case "INTEGER":
            case "LONG":
            case "UINT":
            case "UINT_64":
                return php.TypeLiteral.number(1);
            case "FLOAT":
            case "DOUBLE":
                return php.TypeLiteral.float(1.0);
            case "BOOLEAN":
                return php.TypeLiteral.boolean(true);
            case "STRING":
                return php.TypeLiteral.string("value");
            case "DATE":
                return php.TypeLiteral.datetime("2024-01-01");
            case "DATE_TIME":
                return php.TypeLiteral.datetime("2024-01-01T00:00:00Z");
            case "UUID":
                return php.TypeLiteral.string("d5e9c84f-c2b2-4bf4-b4b0-7ffd7a9ffc32");
            case "BASE_64":
                return php.TypeLiteral.string("SGVsbG8=");
            case "BIG_INTEGER":
                return php.TypeLiteral.string("1000000");
            default:
                assertNever(primitive);
        }
    }

    private generateNamedPlaceholder(named: FernIr.dynamic.NamedType): php.TypeLiteral {
        switch (named.type) {
            case "alias":
                return this.generatePlaceholderValue(named.typeReference);
            case "enum": {
                const firstValue = named.values[0];
                if (firstValue == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.reference(
                    php.codeblock((writer) => {
                        writer.writeNode(
                            php.classReference({
                                name: this.context.getClassName(named.declaration.name),
                                namespace: this.context.getTypesNamespace(named.declaration.fernFilepath)
                            })
                        );
                        writer.write("::");
                        writer.write(this.context.getClassName(firstValue.name));
                        writer.write("->value");
                    })
                );
            }
            case "object":
                return this.convertObject({ object_: named, value: {} });
            case "discriminatedUnion": {
                const firstType = Object.values(named.types)[0];
                if (firstType == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.nop();
            }
            case "undiscriminatedUnion": {
                const firstType = named.types[0];
                if (firstType == null) {
                    return php.TypeLiteral.nop();
                }
                return this.generatePlaceholderValue(firstType);
            }
            default:
                assertNever(named);
        }
    }

    private generateLiteralPlaceholder(literal: FernIr.dynamic.LiteralType): php.TypeLiteral {
        switch (literal.type) {
            case "boolean":
                return php.TypeLiteral.boolean(literal.value);
            case "string":
                return php.TypeLiteral.string(literal.value);
            default:
                assertNever(literal);
        }
    }

    private convertEnum({ enum_, value }: { enum_: FernIr.dynamic.EnumType; value: unknown }): php.TypeLiteral {
        const name = this.getEnumValueName({ enum_, value });
        if (name == null) {
            return php.TypeLiteral.nop();
        }
        return php.TypeLiteral.reference(
            php.codeblock((writer) => {
                writer.writeNode(
                    php.classReference({
                        name: this.context.getClassName(enum_.declaration.name),
                        namespace: this.context.getTypesNamespace(enum_.declaration.fernFilepath)
                    })
                );
                writer.write("::");
                writer.write(name);
                writer.write("->value");
            })
        );
    }

    private getEnumValueName({ enum_, value }: { enum_: FernIr.dynamic.EnumType; value: unknown }): string | undefined {
        if (typeof value !== "string") {
            this.context.errors.add({
                severity: Severity.Critical,
                message: `Expected enum value string, got: ${typeof value}`
            });
            return undefined;
        }
        const enumValue = enum_.values.find((v) => v.wireValue === value);
        if (enumValue == null) {
            this.context.errors.add({
                severity: Severity.Critical,
                message: `An enum value named "${value}" does not exist in this context`
            });
            return undefined;
        }
        return this.context.getClassName(enumValue.name);
    }

    private convertUndiscriminatedUnion({
        undiscriminatedUnion,
        value
    }: {
        undiscriminatedUnion: FernIr.dynamic.UndiscriminatedUnionType;
        value: unknown;
    }): php.TypeLiteral {
        const result = this.findMatchingUndiscriminatedUnionType({
            undiscriminatedUnion,
            value
        });
        if (result == null) {
            return php.TypeLiteral.nop();
        }
        return result;
    }

    private findMatchingUndiscriminatedUnionType({
        undiscriminatedUnion,
        value
    }: {
        undiscriminatedUnion: FernIr.dynamic.UndiscriminatedUnionType;
        value: unknown;
    }): php.TypeLiteral | undefined {
        for (const typeReference of undiscriminatedUnion.types) {
            const errorsBefore = this.context.errors.size();
            try {
                const result = this.convert({ typeReference, value });
                if (php.TypeLiteral.isNop(result)) {
                    this.context.errors.truncate(errorsBefore);
                    continue;
                }
                return result;
            } catch (e) {
                this.context.errors.truncate(errorsBefore);
                continue;
            }
        }
        this.context.errors.add({
            severity: Severity.Critical,
            message: `None of the types in the undiscriminated union matched the given "${typeof value}" value`
        });
        return undefined;
    }

    private convertUnknown({ value }: { value: unknown }): php.TypeLiteral {
        return php.TypeLiteral.unknown(value);
    }

    private convertPrimitive({
        primitive,
        value,
        as
    }: {
        primitive: FernIr.dynamic.PrimitiveTypeV1;
        value: unknown;
        as?: DynamicTypeLiteralMapper.ConvertedAs;
    }): php.TypeLiteral {
        switch (primitive) {
            case "INTEGER":
            case "LONG":
            case "UINT":
            case "UINT_64": {
                const num = this.getValueAsNumber({ value, as });
                if (num == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.number(num);
            }
            case "FLOAT":
            case "DOUBLE": {
                const num = this.getValueAsNumber({ value });
                if (num == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.float(num);
            }
            case "BOOLEAN": {
                const bool = this.getValueAsBoolean({ value, as });
                if (bool == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.boolean(bool);
            }
            case "DATE":
            case "DATE_TIME": {
                const str = this.context.getValueAsString({ value });
                if (str == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.datetime(str);
            }
            case "BASE_64":
            case "UUID":
            case "BIG_INTEGER":
            case "STRING": {
                const str = this.context.getValueAsString({ value });
                if (str == null) {
                    return php.TypeLiteral.nop();
                }
                return php.TypeLiteral.string(str);
            }
            default:
                assertNever(primitive);
        }
    }

    private getValueAsNumber({
        value,
        as
    }: {
        value: unknown;
        as?: DynamicTypeLiteralMapper.ConvertedAs;
    }): number | undefined {
        const num = as === "key" ? (typeof value === "string" ? Number(value) : value) : value;
        return this.context.getValueAsNumber({ value: num });
    }

    private getValueAsBoolean({
        value,
        as
    }: {
        value: unknown;
        as?: DynamicTypeLiteralMapper.ConvertedAs;
    }): boolean | undefined {
        const bool =
            as === "key" ? (typeof value === "string" ? value === "true" : value === "false" ? false : value) : value;
        return this.context.getValueAsBoolean({ value: bool });
    }
}
